import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { navigateFromActionUrl } from "@/shared/lib/notification-navigation";
import { useNavigate } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { useState } from "react";
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
} from "../api/queries";

export function NotificationsPage() {
  const nav = useNavigate({ from: "/app/notifications" });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const { data, isLoading } = useNotificationsQuery({
    limit,
    offset,
    unreadOnly,
  });
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const items = data?.items ?? [];
  const total = data?.meta.total ?? 0;

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ການແຈ້ງເຕືອນ" },
      ]}
    />
  );

  const handleOpen = (
    id: string,
    actionUrl: string | null,
    readAt: string | null,
  ) => {
    if (!readAt) markRead.mutate(id);
    if (actionUrl) navigateFromActionUrl(actionUrl, nav);
  };

  return (
    <>
      <Header>{breadcrumb}</Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <BellIcon className="size-6" />
              ການແຈ້ງເຕືອນ
            </h2>
            <p className="text-muted-foreground">
              ອັບເດດຈາກລະບົບ Camp ແລະ ງານອີເວັ້ນ
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={unreadOnly ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setUnreadOnly((v) => !v);
                setOffset(0);
              }}
            >
              {unreadOnly ? "ສະແດງທັງໝົດ" : "ຍັງບໍ່ອ່ານ"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              ອ່ານທັງໝົດ
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="divide-y p-0">
            {isLoading ? (
              <div className="py-16 text-center text-muted-foreground">
                ກຳລັງໂຫຼດ...
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                ບໍ່ມີການແຈ້ງເຕືອນ
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full flex-col gap-1 px-4 py-4 text-left transition-colors hover:bg-muted/50"
                  onClick={() =>
                    handleOpen(item.id, item.actionUrl, item.readAt)
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={
                            item.readAt
                              ? "font-medium"
                              : "font-semibold"
                          }
                        >
                          {item.title}
                        </span>
                        {!item.readAt ? (
                          <Badge variant="secondary" className="text-xs">
                            ໃໝ່
                          </Badge>
                        ) : null}
                      </div>
                      {item.body ? (
                        <p className="mt-1 text-muted-foreground text-sm">
                          {item.body}
                        </p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-muted-foreground text-xs">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {total > limit ? (
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
            >
              ກ່ອນໜ້າ
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset((o) => o + limit)}
            >
              ຖັດໄປ
            </Button>
          </div>
        ) : null}
      </Main>
    </>
  );
}
